/** Canonical valuation units on materials + user entry units for claim yield. */
export type WeightUnit = 'troy_oz' | 'oz' | 'gram' | 'carat';

export const WEIGHT_UNIT_LABELS: Record<WeightUnit, string> = {
  troy_oz: 'troy oz',
  oz: 'oz (standard)',
  gram: 'g',
  carat: 'ct (per stone)',
};

export const GRAMS_PER_TROY_OZ = 31.1034768;
export const GRAMS_PER_AVOIRDUPOIS_OZ = 28.349523125;

export type CatalogMaterial = {
  materialId: string;
  displayName: string;
  kind: string;
  haulCategory?: string;
  gemTier?: string;
  weightEntryMode: string;
  weightUnit: WeightUnit;
  valuationMethod: string;
  priceKey?: string;
};

export type MaterialLineDraft = {
  id?: string;
  materialId: string;
  weightQty: string;
  inputWeightUnit: WeightUnit;
  proofImagePath?: string;
  proofImageUrl?: string;
};

export function weightLabel(unit: WeightUnit): string {
  return WEIGHT_UNIT_LABELS[unit] ?? unit;
}

/** Units a user may pick when entering yield for a material. */
export function entryWeightUnits(canonical: WeightUnit): WeightUnit[] {
  switch (canonical) {
    case 'troy_oz':
      return ['troy_oz', 'gram', 'oz'];
    case 'gram':
      return ['gram', 'oz', 'troy_oz'];
    case 'carat':
      return ['carat'];
    default:
      return [canonical];
  }
}

export function convertWeight(qty: number, from: WeightUnit, to: WeightUnit): number {
  if (from === to) return qty;
  if (from === 'carat' || to === 'carat') {
    throw new Error('carat cannot be converted');
  }
  const grams = toGrams(qty, from);
  return fromGrams(grams, to);
}

function toGrams(qty: number, u: WeightUnit): number {
  switch (u) {
    case 'gram':
      return qty;
    case 'troy_oz':
      return qty * GRAMS_PER_TROY_OZ;
    case 'oz':
      return qty * GRAMS_PER_AVOIRDUPOIS_OZ;
    default:
      throw new Error(`unsupported unit ${u}`);
  }
}

function fromGrams(grams: number, u: WeightUnit): number {
  switch (u) {
    case 'gram':
      return grams;
    case 'troy_oz':
      return grams / GRAMS_PER_TROY_OZ;
    case 'oz':
      return grams / GRAMS_PER_AVOIRDUPOIS_OZ;
    default:
      throw new Error(`unsupported unit ${u}`);
  }
}

export function formatCanonicalHint(
  qty: number,
  inputUnit: WeightUnit,
  canonical: WeightUnit,
): string | null {
  if (!qty || qty <= 0 || inputUnit === canonical) return null;
  try {
    const converted = convertWeight(qty, inputUnit, canonical);
    return `≈ ${converted.toFixed(4)} ${weightLabel(canonical)} for valuation`;
  } catch {
    return null;
  }
}

export const GRAMS_PER_CARAT = 0.2;

export function gramsToCarats(g: number): number {
  if (g <= 0) return 0;
  return g / GRAMS_PER_CARAT;
}

export function caratsToGrams(ct: number): number {
  if (ct <= 0) return 0;
  return ct * GRAMS_PER_CARAT;
}

export function convertToTroyOz(qty: number, from: 'gram' | 'oz'): number {
  return convertWeight(qty, from, 'troy_oz');
}

export function convertFromKitchenScale(
  qty: number,
  from: 'gram' | 'oz',
): { grams: number; troyOz: number; carats: number } {
  const grams = from === 'gram' ? qty : qty * GRAMS_PER_AVOIRDUPOIS_OZ;
  const troyOz = convertWeight(grams, 'gram', 'troy_oz');
  return { grams, troyOz, carats: gramsToCarats(grams) };
}
